import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box, AppBar, Toolbar, Typography, Stack, Chip, IconButton, Tooltip, useMediaQuery, CircularProgress, Snackbar, Alert,
    MenuItem, ListItemIcon, ListItemText, Divider,
} from "@mui/material";
import Compressor from "compressorjs";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import BarChartIcon from "@mui/icons-material/BarChart";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import livechatApi from "./livechatApi";
import { getAttachmentKind } from "./livechatDisplayUtils";
import { connectLivechatSocket, disconnectLivechatSocket } from "./livechatSocket";
import { LIVECHAT_PARTNER_BRAND, ASSIGNMENT_REASON_TRANSFER, isClosedPaidSession } from "./livechatConstants";
import ChannelRail from "./ChannelRail";
import ConversationList from "./ConversationList";
import ChatPanel from "./ChatPanel";
import CustomerContextPanel from "./CustomerContextPanel";
import SetLabelDialog from "./SetLabelDialog";
import GenerateLinkDialog from "./GenerateLinkDialog";
import LiveChatSettingsDialog from "./LiveChatSettingsDialog";
import LiveChatDashboardDialog from "./LiveChatDashboardDialog";
import MitraContactsDialog from "./MitraContactsDialog";
import LeadSalesDialog from "./LeadSalesDialog";
import LeadAvailabilityToggle from "./LeadAvailabilityToggle";
import OwnershipTabs from "./OwnershipTabs";
import AssignmentBadge from "./AssignmentBadge";
import AgentIdentityMenu, { getLoginIdentity } from "./AgentIdentityMenu";
import useAutoAssign from "./useAutoAssign";
import useLivechatAccess, { LIVECHAT_PERMISSION, isCustomerBrand } from "./useLivechatAccess";
import AccessLevelDialog from "./AccessLevelDialog";
import TransferChatDialog from "./TransferChatDialog";
import PartnerOnlineChip from "./PartnerOnlineChip";
import { playNotificationSound } from "./livechatSound";
import { PASTEL } from "./livechatTheme";
import Global from "../../customlib/Global";

const LIVECHAT_MESSAGES_PAGE_SIZE = 50;
const INBOX_REFRESH_COALESCE_MS = 500;

function compressImage(file) {
    return new Promise((resolve, reject) => {
        new Compressor(file, {
            quality: 0.8,
            maxWidth: 1600,
            maxHeight: 1600,
            success: resolve,
            error: reject,
        });
    });
}

// Halaman baru DEV-1049. Auth ke lotus-be-express reuse Global.get().be_express_token yang sudah
// ada; App Profile yang dipilih saat login ikut dikirim (header x-app-profile-id / auth socket,
// lihat livechatApi.js & livechatSocket.js) untuk menentukan brand yang boleh dilihat.
export default function CrmLiveChat() {
    const isMobile = useMediaQuery("(max-width:900px)");
    const [brands, setBrands] = useState([]);
    const [activeBrand, setActiveBrand] = useState("all");
    const [filter, setFilter] = useState("active");
    const [search, setSearch] = useState("");
    const [sessions, setSessions] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messagesHasMore, setMessagesHasMore] = useState(false);
    const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
    const nextMessagesPageRef = useRef(2);
    const [labelDialogOpen, setLabelDialogOpen] = useState(false);
    const [generatedLink, setGeneratedLink] = useState(null);
    const [unreadTotal, setUnreadTotal] = useState(0);
    const [channelUnread, setChannelUnread] = useState({});
    const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
    const [dashboardDialogOpen, setDashboardDialogOpen] = useState(false);
    const [contactsDialogOpen, setContactsDialogOpen] = useState(false);
    const [leadSalesDialogOpen, setLeadSalesDialogOpen] = useState(false);
    const [accessLevelDialogOpen, setAccessLevelDialogOpen] = useState(false);
    const [contextPanelOpen, setContextPanelOpen] = useState(true);
    const isActiveRef = useRef(true);

    // DEV-1234 — pembagian lead otomatis. Semua elemen UI-nya cuma tampil untuk brand dengan
    // autoAssignEnabled, jadi tampilan brand Corporate & mitra-purchasing tidak berubah.
    const autoAssign = useAutoAssign();
    const { applyAvailability, reloadAvailability } = autoAssign;
    const [ownership, setOwnership] = useState("all");
    const [agentFilter, setAgentFilter] = useState(null);
    const [claiming, setClaiming] = useState(false);
    const [notice, setNotice] = useState(null);
    const viewerId = Global.get()?.user?.id;
    const loginIdentity = getLoginIdentity();
    const autoAssignBrandSet = useMemo(
        () => new Set(brands.filter((b) => b.autoAssignEnabled).map((b) => b.brand)),
        [brands]
    );
    // Role sales (ownOnly): inbox cuma berisi percakapan miliknya, jadi tab kepemilikan tidak relevan.
    const showOwnershipTabs =
        !autoAssign.availability.ownOnly &&
        (autoAssign.availability.eligible || autoAssign.availability.isSupervisor) &&
        (activeBrand === "all" ? autoAssignBrandSet.size > 0 : autoAssignBrandSet.has(activeBrand));

    const socket = useMemo(() => connectLivechatSocket(), []);
    // DEV-1234 — Level Akses Live Chat Customer. Setelah admin mengubah level, room inbox socket
    // (ditentukan server saat connect) ikut diperbarui dengan menyambung ulang, lalu status
    // autoleads (eligible/ownOnly) dimuat ulang.
    const [accessVersion, setAccessVersion] = useState(0);
    const handleAccessChanged = useCallback(() => {
        socket.disconnect();
        socket.connect();
        reloadAvailability();
        setAccessVersion((v) => v + 1); // cakupan lihat bisa berubah -> muat ulang daftar percakapan
    }, [socket, reloadAvailability]);
    const { can, canForSession, access } = useLivechatAccess(socket, handleAccessChanged);
    // MTN-956 — Tim CS: memantau semua chat Selesai (penanda & angka tab Selesai) + Alihkan Chat.
    const isCsTeam = can(LIVECHAT_PERMISSION.CLOSED_VIEW_ALL);
    const [awaitingCsCount, setAwaitingCsCount] = useState(0);
    const [transferDialogOpen, setTransferDialogOpen] = useState(false);
    const [socketConnected, setSocketConnected] = useState(socket.connected);
    const [reconnecting, setReconnecting] = useState(false);

    useEffect(() => {
        // socket.connected sudah bisa true saat mount (koneksi dibuat sebelum effect ini jalan,
        // lihat connectLivechatSocket di atas) — makanya initial state useState juga baca
        // socket.connected langsung, bukan cuma andalkan event "connect" yang tidak akan fire lagi.
        const onConnect = () => { setSocketConnected(true); setReconnecting(false); };
        const onDisconnect = () => { setSocketConnected(false); setReconnecting(false); };
        socket.on("connect", onConnect);
        socket.on("disconnect", onDisconnect);
        socket.on("connect_error", onDisconnect);
        return () => {
            socket.off("connect", onConnect);
            socket.off("disconnect", onDisconnect);
            socket.off("connect_error", onDisconnect);
        };
    }, [socket]);

    // Socket.io sudah auto-reconnect sendiri di background, tapi kalau attempt-nya sedang jeda
    // (backoff) atau gagal terus, agent bisa lama menunggu tanpa kepastian — tombol ini
    // memicu percobaan connect() manual, langsung (skip sisa waktu backoff yang sedang berjalan).
    const handleReconnect = useCallback(() => {
        if (socket.connected || reconnecting) return;
        setReconnecting(true);
        socket.connect();
    }, [socket, reconnecting]);

    useEffect(() => {
        // Suara notifikasi dibuat lebih keras (loud) saat tab sedang tidak difokus agent —
        // visible saja tidak cukup (tab masih "visible" saat alt-tab ke app lain), makanya
        // digabung dengan window focus/blur. Mirror dari livechat-customer-fe/ChatScreen.jsx.
        const updateActivityState = () => {
            isActiveRef.current = document.visibilityState === "visible" && document.hasFocus();
        };
        updateActivityState();
        document.addEventListener("visibilitychange", updateActivityState);
        window.addEventListener("focus", updateActivityState);
        window.addEventListener("blur", updateActivityState);
        return () => {
            document.removeEventListener("visibilitychange", updateActivityState);
            window.removeEventListener("focus", updateActivityState);
            window.removeEventListener("blur", updateActivityState);
        };
    }, []);

    // Guard "request terbaru menang" (pola sama dgn MessageList): ganti filter/brand/search cepat
    // atau refresh dari socket yang tumpang tindih -> respons yang datang belakangan tapi milik
    // request lama tidak boleh menimpa daftar yang lebih baru.
    const inboxRequestRef = useRef(0);
    const channelUnreadRequestRef = useRef(0);
    const selectRequestRef = useRef(0);
    // Disinkronkan tiap render — dipakai fetch async untuk memastikan sesi yang dibuka belum berganti.
    const selectedSessionIdRef = useRef(null);
    selectedSessionIdRef.current = selectedSession?._id ?? null;

    const loadInbox = useCallback(async () => {
        const requestId = ++inboxRequestRef.current;
        const params = {
            brand: activeBrand !== "all" ? activeBrand : undefined,
            status: filter === "closed" ? "closed" : filter === "active" ? "active" : undefined,
            unreadOnly: filter === "unread" ? "true" : undefined,
            search: search || undefined,
            limit: 50,
            ownership: showOwnershipTabs && ownership !== "all" ? ownership : undefined,
            agentId: showOwnershipTabs && agentFilter != null ? agentFilter : undefined,
        };
        try {
            const { data } = await livechatApi.listInbox(params);
            if (requestId !== inboxRequestRef.current) return;
            setSessions(data.data || []);
            // MTN-956 — chat Selesai tidak dihitung belum dibaca (chat Paid tetap Selesai saat dibalas).
            setUnreadTotal((data.data || []).filter((s) => s.unreadByAgent && s.status !== "closed").length);
        } catch (error) {
            // Daftar lama tetap ditampilkan; refresh berikutnya (socket/aksi agent) akan mencoba lagi.
            console.error("Error in CrmLiveChat.loadInbox:", error);
        }
        // accessVersion: Level Akses diubah admin -> daftar dimuat ulang dengan cakupan baru
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeBrand, filter, search, showOwnershipTabs, ownership, agentFilter, accessVersion]);

    const loadBrands = useCallback(() => {
        livechatApi
            .listBrands()
            .then(({ data }) => setBrands(data.data?.brands || []))
            .catch((error) => {
                console.error("Error in CrmLiveChat.loadBrands:", error);
                setNotice({ severity: "error", text: "Gagal memuat daftar kanal — coba muat ulang halaman" });
            });
    }, []);

    // Unread count per-kanal untuk ChannelRail — independen dari filter/search/activeBrand
    // yang sedang aktif di ConversationList (rail harus tetap menampilkan total unread SEMUA
    // kanal, bukan cuma kanal yang lagi dipilih). limit:1 karena hanya `additional_data.total`
    // (bukan daftar sesi-nya) yang dipakai di sini.
    const loadChannelUnread = useCallback(async () => {
        const requestId = ++channelUnreadRequestRef.current;
        const targets = [{ key: "all", brand: undefined }, ...brands.map((b) => ({ key: b.brand, brand: b.brand }))];
        const entries = await Promise.all(
            targets.map(({ key, brand }) =>
                livechatApi
                    .listInbox({ brand, unreadOnly: "true", limit: 1 })
                    .then(({ data }) => [key, data.additional_data?.total || 0])
                    .catch(() => [key, 0])
            )
        );
        if (requestId !== channelUnreadRequestRef.current) return;
        setChannelUnread(Object.fromEntries(entries));
    }, [brands]);

    // MTN-956 — angka di tab Selesai (khusus Tim CS): chat Paid Selesai yang dibalas customer dan
    // belum dicek, mengikuti kanal yang sedang dipilih. Ikut di-refresh bersama unread per kanal.
    const awaitingCsRequestRef = useRef(0);
    const loadAwaitingCsCount = useCallback(async () => {
        const requestId = ++awaitingCsRequestRef.current;
        if (!isCsTeam) {
            setAwaitingCsCount(0);
            return;
        }
        try {
            const { data } = await livechatApi.listInbox({
                brand: activeBrand !== "all" ? activeBrand : undefined,
                status: "closed",
                awaitingCs: "true",
                limit: 1,
            });
            if (requestId !== awaitingCsRequestRef.current) return;
            setAwaitingCsCount(data.additional_data?.total || 0);
        } catch (error) {
            console.error("Error in CrmLiveChat.loadAwaitingCsCount:", error);
        }
    }, [isCsTeam, activeBrand]);

    // Refresh daftar & unread per kanal yang dipicu event socket digabung dalam satu jendela
    // INBOX_REFRESH_COALESCE_MS: tiap refresh = 1 + N request (inbox + unread per brand), dulu
    // dipanggil langsung di SETIAP event (inbox:session:new/updated, session:assigned) sehingga
    // chat yang ramai memicu ratusan request. Timer TIDAK di-reset oleh event berikutnya (bukan
    // debounce murni), supaya arus event yang terus-menerus tetap ter-refresh berkala, bukan
    // tertunda selamanya. Ref dipakai supaya listener socket tidak didaftar ulang tiap filter berubah.
    const loadInboxRef = useRef(loadInbox);
    loadInboxRef.current = loadInbox;
    const loadChannelUnreadRef = useRef(loadChannelUnread);
    loadChannelUnreadRef.current = loadChannelUnread;
    const loadAwaitingCsCountRef = useRef(loadAwaitingCsCount);
    loadAwaitingCsCountRef.current = loadAwaitingCsCount;
    const inboxRefreshTimerRef = useRef(null);

    const scheduleInboxRefresh = useCallback(() => {
        if (inboxRefreshTimerRef.current) return;
        inboxRefreshTimerRef.current = setTimeout(() => {
            inboxRefreshTimerRef.current = null;
            loadInboxRef.current();
            loadChannelUnreadRef.current();
            loadAwaitingCsCountRef.current();
        }, INBOX_REFRESH_COALESCE_MS);
    }, []);

    useEffect(() => () => clearTimeout(inboxRefreshTimerRef.current), []);

    useEffect(() => {
        loadBrands();
    }, [loadBrands]);

    useEffect(() => {
        loadInbox();
    }, [loadInbox]);

    useEffect(() => {
        loadChannelUnread();
    }, [loadChannelUnread]);

    useEffect(() => {
        loadAwaitingCsCount();
        // accessVersion: hak Tim CS bisa berubah setelah admin mengubah Level Akses
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadAwaitingCsCount, accessVersion]);

    useEffect(() => {
        document.title = unreadTotal > 0 ? `(${unreadTotal}) Live Chat` : "Live Chat";
    }, [unreadTotal]);

    useEffect(() => {
        // Sesi baru (inbox:session:new) selalu berarti pesan pertama dari customer yang belum
        // dibaca (unreadByAgent selalu true saat sesi dibuat, lihat livechat.session.service.ts).
        // Untuk inbox:session:updated, backend TIDAK menyertakan `sender` di payload — tapi
        // unreadByAgent hanya bernilai true kalau update ini berasal dari pesan customer yang
        // belum ditandai dibaca (pesan agent sendiri tidak mengubah unreadByAgent, lihat
        // livechat.message.service.ts#createMessage), jadi flag ini sudah cukup untuk membedakan
        // "customer baru menulis" dari "echo pesan balasan agent sendiri" tanpa perlu field baru.
        const onNewSession = () => {
            scheduleInboxRefresh();
            playNotificationSound(!isActiveRef.current);
        };
        // MTN-956 — update chat Selesai (customer membalas chat Paid yang sudah Selesai, pantauan Tim
        // CS) tidak pernah berbunyi: chat itu tidak kembali ke sales.
        const onSessionUpdated = (payload) => {
            scheduleInboxRefresh();
            if (payload?.unreadByAgent && payload?.status !== "closed") playNotificationSound(!isActiveRef.current);
        };
        // DEV-1234 — percakapan ditugaskan ke saya (otomatis bergiliran atau hasil klaim sendiri).
        // MTN-956 — atau dialihkan Tim CS dari tab Selesai (reason "transfer").
        const onSessionAssigned = (payload) => {
            scheduleInboxRefresh();
            if (payload?.reason === ASSIGNMENT_REASON_TRANSFER) {
                playNotificationSound(!isActiveRef.current);
                setNotice({
                    severity: "info",
                    text: `${payload?.transferredByName || "Tim CS"} mengalihkan percakapan ke Anda: ${payload?.customerName || "pelanggan"}`,
                });
                return;
            }
            if (payload?.reason !== "claim") {
                playNotificationSound(!isActiveRef.current);
                setNotice({ severity: "info", text: `Percakapan baru ditugaskan ke Anda: ${payload?.customerName || "pelanggan"}` });
            }
        };
        // DEV-1238 — admin meng-override autoleads saya dari Kelola Sales.
        const onAvailabilityChanged = (payload) => {
            // Whitelist — admin memasukkan/mengeluarkan saya: toggle langsung disembunyikan kalau
            // dikeluarkan, lalu `eligible` dihitung ulang backend.
            if (payload?.reason === "membership") {
                applyAvailability(payload.member ? payload : { ...payload, eligible: false });
                reloadAvailability();
                setNotice({
                    severity: payload.member ? "info" : "warning",
                    text: payload.member
                        ? "Anda ditambahkan ke autoleads Live Chat oleh admin. Nyalakan \"Autoleads\" untuk mulai menerima lead."
                        : "Anda dikeluarkan dari autoleads Live Chat oleh admin",
                });
                return;
            }
            applyAvailability(payload);
            // DEV-1234 — autoleads default ON setiap login (tidak berlaku kalau dikunci admin).
            if (payload?.reason === "login") {
                setNotice({ severity: "info", text: "Autoleads dinyalakan otomatis saat login. Matikan kalau sedang tidak ingin menerima lead." });
                return;
            }
            setNotice({
                severity: payload?.lockedByAdmin ? "warning" : "info",
                text: payload?.lockedByAdmin
                    ? `Autoleads Anda dinonaktifkan oleh ${payload?.lockedByName || "admin"}`
                    : "Autoleads Anda dinyalakan oleh admin",
            });
        };
        socket.on("inbox:session:new", onNewSession);
        socket.on("inbox:session:updated", onSessionUpdated);
        socket.on("session:assigned", onSessionAssigned);
        socket.on("agent:availability:changed", onAvailabilityChanged);
        return () => {
            socket.off("inbox:session:new", onNewSession);
            socket.off("inbox:session:updated", onSessionUpdated);
            socket.off("session:assigned", onSessionAssigned);
            socket.off("agent:availability:changed", onAvailabilityChanged);
        };
    }, [socket, scheduleInboxRefresh, applyAvailability, reloadAvailability]);

    useEffect(() => {
        return () => disconnectLivechatSocket();
    }, []);

    // Dipakai baik oleh optimistic-update di handleSendMessage/handleSendAttachment maupun oleh
    // listener socket "conversation:message:new" — dedupe by _id supaya pesan yang sama tidak
    // dobel dirender kalau backend suatu saat juga meng-echo pesan milik sendiri lewat socket.
    const appendMessage = useCallback((msg) => {
        if (!msg) return;
        setMessages((prev) => (msg._id && prev.some((m) => m._id === msg._id)) ? prev : [...prev, msg]);
    }, []);

    const handleSelectSession = useCallback(async (session) => {
        const requestId = ++selectRequestRef.current;
        // Respons dianggap basi kalau agent sudah memilih sesi lain (token) atau menutup panel
        // (selectedSessionIdRef) sebelum request ini selesai — jangan timpa pesan sesi yang sedang dibuka.
        const isStale = () => requestId !== selectRequestRef.current || selectedSessionIdRef.current !== session._id;

        setSelectedSession(session);
        setMessages([]);
        setMessagesHasMore(false);
        try {
            const { data } = await livechatApi.getSessionDetail(session._id, { page: 1, limit: LIVECHAT_MESSAGES_PAGE_SIZE });
            if (isStale()) return;
            setMessages(data.data?.messages || []);
            setMessagesHasMore(!!data.data?.hasMoreMessages);
            nextMessagesPageRef.current = 2;
        } catch (error) {
            if (isStale()) return;
            console.error("Error in CrmLiveChat.handleSelectSession:", error);
            setNotice({ severity: "error", text: error?.response?.data?.message || "Gagal memuat percakapan" });
            return;
        }
        // markRead gagal tidak boleh membatalkan tampilan percakapan; daftar tetap di-refresh.
        livechatApi
            .markRead(session._id)
            .catch((error) => console.error("Error in CrmLiveChat.markRead:", error))
            .finally(() => {
                loadInbox();
                loadChannelUnread();
                // MTN-956 — Tim CS membuka chat bertanda -> penanda dihapus backend, angka ikut berkurang.
                if (session.awaitingCsSince) loadAwaitingCsCount();
            });
        // Join room percakapan dilakukan effect `selectedSession` di bawah (bukan di sini), supaya
        // ikut terulang setiap kali selectedSession diganti objek baru (mis. setelah Set Label /
        // Ambil) dan setelah socket reconnect.
    }, [loadInbox, loadChannelUnread, loadAwaitingCsCount]);

    // Dipanggil ChatPanel saat pengguna scroll mentok ke atas daftar pesan — memuat halaman
    // pesan lebih LAMA (page 2, 3, ...) dan menaruhnya di AWAL array (bukan replace total seperti
    // handleSelectSession), dedupe by _id jaga-jaga kalau ada overlap antar halaman.
    const handleLoadOlderMessages = useCallback(async () => {
        if (!selectedSession || loadingOlderMessages || !messagesHasMore) return;
        setLoadingOlderMessages(true);
        const sessionId = selectedSession._id;
        try {
            const page = nextMessagesPageRef.current;
            const { data } = await livechatApi.getSessionDetail(sessionId, { page, limit: LIVECHAT_MESSAGES_PAGE_SIZE });
            // Agent pindah sesi selagi halaman lama dimuat -> jangan tempelkan pesan sesi A ke sesi B.
            if (selectedSessionIdRef.current !== sessionId) return;
            const older = data.data?.messages || [];
            setMessages((prev) => {
                const existingIds = new Set(prev.map((m) => m._id));
                const merged = older.filter((m) => !existingIds.has(m._id));
                return [...merged, ...prev];
            });
            setMessagesHasMore(!!data.data?.hasMoreMessages);
            nextMessagesPageRef.current = page + 1;
        } catch (error) {
            console.error("Error in CrmLiveChat.handleLoadOlderMessages:", error);
        } finally {
            setLoadingOlderMessages(false);
        }
    }, [selectedSession, loadingOlderMessages, messagesHasMore]);

    useEffect(() => {
        if (!selectedSession) return;

        const onNewMessage = (msg) => {
            if (msg.sessionId?.toString() !== selectedSession._id?.toString()) return;
            // Pesan staff ("agent" di channel customer, "purchasing" di channel mitra-purchasing).
            // Backend meng-echo event ini ke SELURUH room termasuk socket pengirimnya. Pesan milik
            // agent yang login sudah tampil lewat jalur optimistic (attemptSendMessage /
            // attemptSendAttachment) dan bubble-nya masih ber-_id sementara (tempId) saat echo tiba,
            // jadi dedupe by _id tidak match -> echo milik SENDIRI diabaikan supaya tidak dobel.
            // Pesan staff LAIN (agent lain / Leader-SPV di sesi yang sama) tetap ditampilkan —
            // dulu ikut dibuang sehingga baru muncul setelah sesi dibuka ulang. Keterbatasan: pesan
            // dari tab lain milik user yang SAMA masih dianggap echo sendiri.
            if (msg.sender === "agent" || msg.sender === "purchasing") {
                if (viewerId != null && Number(msg.senderAgentId) === Number(viewerId)) return;
                appendMessage(msg);
                return;
            }
            appendMessage(msg);
            // Agent sedang membuka percakapan ini saat pesan baru dari customer masuk -> tandai
            // dibaca langsung, jangan tunggu pilih ulang sesi (markRead sebelumnya cuma dipanggil
            // sekali saat handleSelectSession, jadi pesan yang masuk setelah itu tidak pernah
            // ditandai dibaca selama sesi masih terbuka).
            livechatApi.markRead(selectedSession._id).catch(() => {});
        };
        // Customer/mitra membuka kembali percakapan -> tandai pesan milik staff sebagai dibaca (✓✓),
        // tanpa perlu reload/refetch (lihat livechat.message.service.ts#markRead di backend).
        const onReadAck = ({ by, readAt, reader }) => {
            // DEV-1423 — staff purchasing (termasuk diri sendiri) membaca pesan mitra: perbarui
            // "Dibaca oleh" tanpa refetch. Hanya pesan non-staff yang belum mencatat staff ini.
            if (by === "agent" && reader?.agentId != null) {
                setMessages((prev) => prev.map((m) => (
                    m.sender !== "agent" && m.sender !== "purchasing" && m.sender !== "system" &&
                    !(m.readByAgents || []).some((r) => Number(r.agentId) === Number(reader.agentId))
                        ? { ...m, readByAgents: [...(m.readByAgents || []), { ...reader, readAt }] }
                        : m
                )));
                return;
            }
            if (by !== "customer") return;
            setMessages((prev) => prev.map((m) => (
                (m.sender === "agent" || m.sender === "purchasing") && !m.readByCustomerAt ? { ...m, readByCustomerAt: readAt } : m
            )));
        };

        // DEV-1424 — app mitra terbuka: pesan staff yang belum sampai jadi ✓✓ abu tanpa refetch.
        const onDeliveredAck = ({ deliveredAt }) => {
            setMessages((prev) => prev.map((m) => (
                (m.sender === "agent" || m.sender === "purchasing") && !m.readByCustomerAt && !m.deliveredToCustomerAt
                    ? { ...m, deliveredToCustomerAt: deliveredAt }
                    : m
            )));
        };

        // Fix: dulu join cuma dipanggil sekali di handleSelectSession, sementara cleanup di bawah
        // selalu emit leave setiap kali selectedSession berganti objek (mis. onSaved Set Label) —
        // agent jadi keluar dari room & pesan baru tidak tampil live sampai sesi dipilih ulang.
        // Room juga hilang di server setiap socket reconnect, jadi join ulang saat "connect".
        const joinConversation = () => {
            socket.emit("conversation:join", { brand: selectedSession.brand, phone: selectedSession.customerPhone });
        };
        joinConversation();

        socket.on("conversation:message:new", onNewMessage);
        socket.on("conversation:read:ack", onReadAck);
        socket.on("conversation:delivered:ack", onDeliveredAck);
        socket.on("connect", joinConversation);
        return () => {
            socket.off("conversation:message:new", onNewMessage);
            socket.off("conversation:read:ack", onReadAck);
            socket.off("conversation:delivered:ack", onDeliveredAck);
            socket.off("connect", joinConversation);
            socket.emit("conversation:leave", { brand: selectedSession.brand, phone: selectedSession.customerPhone });
        };
    }, [socket, selectedSession, appendMessage, viewerId]);

    // Kirim pesan teks dibuat optimistic (bubble "sending" langsung tampil di daftar, di-update
    // jadi bubble asli dari response REST kalau sukses, atau "failed" + tombol retry kalau gagal)
    // — sebelumnya kegagalan di sini silent total: input sudah keburu dikosongkan di ChatPanel
    // dan tidak ada bubble apa pun yang menandai pesannya hilang.
    const attemptSendMessage = useCallback(async (tempId, sessionId, text) => {
        setMessages((prev) => prev.map((m) => (m._id === tempId ? { ...m, deliveryStatus: "sending" } : m)));
        try {
            const { data } = await livechatApi.sendMessage(sessionId, { text });
            const sent = data.data?.message || data.data;
            setMessages((prev) => prev.map((m) => (m._id === tempId ? { ...sent, deliveryStatus: "sent" } : m)));
        } catch (error) {
            console.error("Error in CrmLiveChat.attemptSendMessage:", error);
            setMessages((prev) => prev.map((m) => (m._id === tempId ? { ...m, deliveryStatus: "failed" } : m)));
        }
    }, []);

    const handleSendMessage = useCallback((text) => {
        if (!selectedSession) return;
        const sessionId = selectedSession._id;
        const tempId = `temp-${Date.now()}`;
        setMessages((prev) => [...prev, {
            _id: tempId, sender: "agent", text, deliveryStatus: "sending", created_at: new Date().toISOString(),
        }]);
        attemptSendMessage(tempId, sessionId, text);
    }, [selectedSession, attemptSendMessage]);

    // Kompresi dipindah ke sini (dari ChatPanel) supaya kegagalannya (bukan cuma upload/kirim)
    // ikut tertangkap oleh bubble optimistic yang sama, konsisten dengan pola
    // livechat-customer-fe/ChatScreen.jsx#handleSendAttachment (bubble "sending" dibuat DULU,
    // baru kompres+upload+kirim dibungkus try/catch — bukan kompres dulu baru bikin bubble).
    // Kompresi (compressorjs) hanya berlaku utk gambar — video/dokumen diunggah apa adanya.
    const attemptSendAttachment = useCallback(async (tempId, sessionId, file, caption, previewUrl) => {
        setMessages((prev) => prev.map((m) => (m._id === tempId ? { ...m, deliveryStatus: "sending" } : m)));
        try {
            const kind = getAttachmentKind(file.type);
            const uploadPayload = kind === "image" ? await compressImage(file) : file;
            const { data: uploadRes } = await livechatApi.uploadFile(uploadPayload);
            const url = uploadRes.data?.url;
            const attachmentType = uploadRes.data?.attachmentType;
            const attachmentName = uploadRes.data?.fileName;
            const { data } = await livechatApi.sendMessage(sessionId, {
                text: caption || undefined, attachmentUrl: url, attachmentType, attachmentName,
            });
            const sent = data.data?.message || data.data;
            setMessages((prev) => prev.map((m) => (m._id === tempId ? { ...sent, deliveryStatus: "sent" } : m)));
            URL.revokeObjectURL(previewUrl);
        } catch (error) {
            console.error("Error in CrmLiveChat.attemptSendAttachment:", error);
            setMessages((prev) => prev.map((m) => (m._id === tempId ? { ...m, deliveryStatus: "failed" } : m)));
        }
    }, []);

    const handleSendAttachment = useCallback((file, caption, previewUrl) => {
        if (!selectedSession) return;
        const sessionId = selectedSession._id;
        const tempId = `temp-${Date.now()}`;
        setMessages((prev) => [...prev, {
            _id: tempId, sender: "agent", text: caption || undefined,
            attachmentUrl: previewUrl, attachmentType: getAttachmentKind(file.type), attachmentName: file.name,
            deliveryStatus: "sending", created_at: new Date().toISOString(),
            // disimpan di bubble sendiri (bukan closure) supaya retry tetap bisa dipakai walau
            // komponen re-render/unmount di antara percobaan pertama & retry.
            _retryFile: file, _retryCaption: caption,
        }]);
        attemptSendAttachment(tempId, sessionId, file, caption, previewUrl);
    }, [selectedSession, attemptSendAttachment]);

    // Dipanggil dari tombol retry di bubble "Gagal terkirim" (ChatPanel). Sengaja pakai
    // selectedSession._id SAAT RETRY diklik (bukan menyimpan sessionId pesan gagal) — pesan
    // gagal cuma bisa terlihat & di-retry selama percakapan yang sama masih dibuka, karena
    // `messages` di-replace total tiap kali pindah sesi (lihat handleSelectSession).
    const handleRetry = useCallback((msg) => {
        if (msg.deliveryStatus !== "failed" || !selectedSession) return;
        if (msg._retryFile) {
            attemptSendAttachment(msg._id, selectedSession._id, msg._retryFile, msg._retryCaption, msg.attachmentUrl);
        } else {
            attemptSendMessage(msg._id, selectedSession._id, msg.text);
        }
    }, [selectedSession, attemptSendMessage, attemptSendAttachment]);

    // Setelah "Selesai", jangan diam di percakapan yang baru ditutup (masih memungkinkan agent
    // ngobrol lagi dari situ & auto-reopen sesi, membingungkan alurnya) — pindah otomatis ke
    // percakapan berikutnya di list yang sedang tampil, atau ke state kosong kalau memang tidak
    // ada percakapan lain (placeholder "Pilih percakapan..." di ChatPanel sudah menangani itu).
    const handleCloseSession = async () => {
        const closingId = selectedSession._id;
        try {
            await livechatApi.closeSession(closingId);
        } catch (error) {
            console.error("Error in CrmLiveChat.handleCloseSession:", error);
            setNotice({ severity: "error", text: error?.response?.data?.message || "Gagal menandai percakapan selesai" });
            return;
        }

        const closedIndex = sessions.findIndex((s) => s._id === closingId);
        const remaining = sessions.filter((s) => s._id !== closingId);
        const nextSession = remaining[closedIndex] ?? remaining[closedIndex - 1] ?? null;

        if (nextSession) {
            handleSelectSession(nextSession);
        } else {
            setSelectedSession(null);
            setMessages([]);
        }
        loadInbox();
    };

    // Tutup panel percakapan tanpa mengubah status sesi. Effect `selectedSession` otomatis emit
    // conversation:leave; respons fetch yang masih berjalan diabaikan (selectedSessionIdRef jadi null).
    const handleCloseChat = useCallback(() => {
        setSelectedSession(null);
        setMessages([]);
        setMessagesHasMore(false);
    }, []);

    const handleGenerateLink = async () => {
        try {
            const { data } = await livechatApi.generateLink(selectedSession._id);
            setGeneratedLink(data.data?.url || "");
        } catch (error) {
            console.error("Error in CrmLiveChat.handleGenerateLink:", error);
            setNotice({ severity: "error", text: error?.response?.data?.message || "Gagal membuat link lanjut chat" });
        }
    };

    // DEV-1234 AC-5 — ambil manual percakapan Unassigned. Kalau keburu diambil sales lain (409),
    // sesi itu sudah tidak boleh dilihat lagi -> tutup panel & refresh daftar.
    const canClaimSelected =
        !!selectedSession &&
        selectedSession.assignedAgentId == null &&
        autoAssignBrandSet.has(selectedSession.brand) &&
        autoAssign.availability.eligible &&
        !autoAssign.availability.ownOnly &&
        // MTN-956 — chat Paid yang sudah Selesai hanya kembali ke sales lewat Alihkan Chat Tim CS.
        !isClosedPaidSession(selectedSession) &&
        canForSession(selectedSession, LIVECHAT_PERMISSION.SESSION_CLAIM);

    // MTN-956 — Alihkan Chat: Tim CS, chat di tab Selesai, brand customer (bukan corporate/mitra).
    const canTransferSelected =
        !!selectedSession &&
        selectedSession.status === "closed" &&
        isCustomerBrand(selectedSession.brand) &&
        can(LIVECHAT_PERMISSION.SESSION_TRANSFER);

    // Setelah dialihkan, chat pindah ke sales tujuan (aktif) -> keluar dari tab Selesai; panel ditutup.
    // Error dilempar balik supaya TransferChatDialog menampilkannya & tetap terbuka.
    const handleTransferSession = async ({ toAgentId, note }) => {
        if (!selectedSession) return;
        try {
            const { data } = await livechatApi.transferSession(selectedSession._id, { toAgentId, note });
            const target = data.data?.assignedAgentName || "sales tujuan";
            setTransferDialogOpen(false);
            setSelectedSession(null);
            setMessages([]);
            setNotice({ severity: "success", text: `Percakapan dialihkan ke ${target}` });
        } catch (error) {
            if (error?.response?.status === 409) {
                setTransferDialogOpen(false);
                setNotice({ severity: "warning", text: error?.response?.data?.message || "Percakapan sudah tidak berstatus Selesai" });
            } else {
                throw error;
            }
        } finally {
            loadInbox();
            loadChannelUnread();
            loadAwaitingCsCount();
        }
    };

    const handleClaimSession = async () => {
        if (!selectedSession || claiming) return;
        setClaiming(true);
        try {
            const { data } = await livechatApi.claimSession(selectedSession._id);
            setSelectedSession(data.data || selectedSession);
            setNotice({ severity: "success", text: "Percakapan berhasil diambil" });
        } catch (error) {
            const message = error?.response?.data?.message || "Gagal mengambil percakapan";
            setNotice({ severity: "warning", text: message });
            if (error?.response?.status === 409) {
                setSelectedSession(null);
                setMessages([]);
            }
        } finally {
            setClaiming(false);
            loadInbox();
            loadChannelUnread();
        }
    };

    // DEV-1197 — dipanggil MitraContactsDialog setelah sesi berhasil dibuat/di-resume via
    // startSessionToMitra; reuse handleSelectSession supaya alur (load messages, markRead,
    // join room socket) sama persis dengan memilih sesi dari ConversationList.
    const handleMitraSessionStarted = useCallback((result) => {
        setContactsDialogOpen(false);
        if (result?.session) handleSelectSession(result.session);
        loadInbox();
        loadChannelUnread();
    }, [handleSelectSession, loadInbox, loadChannelUnread]);

    const canAccessMitraContacts = brands.some((b) => b.brand === LIVECHAT_PARTNER_BRAND);

    // DEV-1423 — "Purchasing online": muat awal + setiap socket (re)connect, lalu ikuti event
    // partner:staff:presence (dikirim backend tiap staff purchasing connect/disconnect).
    const [partnerStaff, setPartnerStaff] = useState([]);
    useEffect(() => {
        if (!canAccessMitraContacts) return undefined;
        let cancelled = false;
        const load = () => {
            livechatApi
                .listOnlinePartnerStaff()
                .then(({ data }) => { if (!cancelled) setPartnerStaff(data.data || []); })
                .catch((error) => console.error("Error in CrmLiveChat.listOnlinePartnerStaff:", error));
        };
        const onPresence = (payload) => setPartnerStaff(payload?.staff || []);
        load();
        socket.on("connect", load);
        socket.on("partner:staff:presence", onPresence);
        return () => {
            cancelled = true;
            socket.off("connect", load);
            socket.off("partner:staff:presence", onPresence);
        };
    }, [socket, canAccessMitraContacts]);

    // DEV-1423 — "Ditangani oleh" di header & panel info ikut versi terbaru dari daftar percakapan
    // (diperbarui lewat inbox:session:updated), tanpa mengganti identitas selectedSession — effect
    // room percakapan bergantung padanya (leave/join ulang setiap kali objeknya berganti).
    const listedSelected = selectedSession ? sessions.find((s) => s._id === selectedSession._id) : null;
    const panelSession = useMemo(() => {
        if (!selectedSession || !listedSelected || selectedSession.brand !== LIVECHAT_PARTNER_BRAND) return selectedSession;
        if (listedSelected.lastHandledByAgentId === selectedSession.lastHandledByAgentId &&
            listedSelected.lastHandledByAgentName === selectedSession.lastHandledByAgentName) return selectedSession;
        return {
            ...selectedSession,
            lastHandledByAgentId: listedSelected.lastHandledByAgentId,
            lastHandledByAgentName: listedSelected.lastHandledByAgentName,
        };
    }, [selectedSession, listedSelected]);

    const handleLogout = () => {
        disconnectLivechatSocket();
        localStorage.removeItem("auth");
        localStorage.removeItem("selectedAppProfile");
        window.location.href = "/";
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
            <AppBar position="static" color="inherit" elevation={0} sx={{ borderBottom: `1px solid ${PASTEL.border}`, backgroundColor: PASTEL.surface }}>
                <Toolbar variant="dense" sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1.5, sm: 3 }, gap: { xs: 1, sm: 2.5 } }}>
                    {/* Boleh menyusut (minWidth 0) supaya caption terpotong ellipsis alih-alih mendorong
                        AppBar melebihi lebar layar di HP. */}
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: "1 1 auto", minWidth: 0 }}>
                        <Box
                            sx={{
                                width: 36, height: 36, borderRadius: 2, flexShrink: 0,
                                display: { xs: "none", sm: "flex" }, alignItems: "center", justifyContent: "center",
                                backgroundColor: PASTEL.primarySoft,
                            }}
                        >
                            <ChatBubbleOutlineIcon sx={{ color: PASTEL.primary, fontSize: 20 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            {/* Di layar sempit blok identitas di kanan cuma avatar, jadi judul diganti nama user
                                dan caption-nya App Profile — dua-duanya tetap terlihat tanpa membuka menu. */}
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                <Typography
                                    variant={isMobile ? "subtitle2" : "subtitle1"}
                                    fontWeight={700}
                                    lineHeight={1.2}
                                    noWrap
                                    sx={{ minWidth: 0 }}
                                >
                                    {isMobile ? loginIdentity.userName : "Live Chat"}
                                </Typography>
                                {unreadTotal > 0 && (
                                    <Chip
                                        size="small"
                                        color="error"
                                        label={unreadTotal}
                                        sx={{ height: 18, fontSize: 10, fontWeight: 700, flexShrink: 0 }}
                                    />
                                )}
                            </Stack>
                            {isMobile ? (
                                <Typography variant="caption" color="text.secondary" noWrap display="block">
                                    {loginIdentity.appProfileLabel}
                                </Typography>
                            ) : (
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {sessions.length} percakapan
                                </Typography>
                            )}
                        </Box>
                    </Stack>

                    {autoAssign.availability.eligible && (
                        <LeadAvailabilityToggle
                            checked={autoAssign.availability.acceptingLeads}
                            saving={autoAssign.saving}
                            onChange={autoAssign.setAcceptingLeads}
                            locked={autoAssign.availability.lockedByAdmin}
                            lockedByName={autoAssign.availability.lockedByName}
                            compact={isMobile}
                        />
                    )}

                    {canAccessMitraContacts && <PartnerOnlineChip staff={partnerStaff} compact={isMobile} />}

                    <Tooltip title={
                        socketConnected ? "Terhubung ke server"
                            : reconnecting ? "Sedang mencoba menyambungkan ulang..."
                                : "Terputus dari server — klik untuk menyambungkan ulang"
                    }>
                        <Chip
                            size="small"
                            onClick={socketConnected ? undefined : handleReconnect}
                            icon={
                                reconnecting ? (
                                    <CircularProgress size={10} thickness={6} sx={{ color: "#c62828", ml: "8px !important" }} />
                                ) : socketConnected ? (
                                    <Box
                                        sx={{
                                            width: 8, height: 8, borderRadius: "50%", ml: "8px !important",
                                            backgroundColor: "#2e7d32",
                                        }}
                                    />
                                ) : (
                                    <RefreshRoundedIcon sx={{ fontSize: 14, ml: "4px !important", color: "#c62828" }} />
                                )
                            }
                            // Di HP saat terhubung cukup titik hijau (keterangan di tooltip) supaya AppBar muat;
                            // status putus/menyambung tetap berteks karena perlu ditindaklanjuti.
                            label={socketConnected ? (isMobile ? undefined : "Terhubung") : reconnecting ? "Menyambungkan..." : "Terputus"}
                            aria-label={socketConnected ? "Terhubung" : undefined}
                            sx={{
                                flexShrink: 0,
                                fontWeight: 600,
                                color: socketConnected ? "#2e7d32" : "#c62828",
                                backgroundColor: socketConnected ? "rgba(46,125,50,0.1)" : "rgba(198,40,40,0.1)",
                                cursor: socketConnected ? "default" : "pointer",
                                "& .MuiChip-icon": { mr: -0.5 },
                                "&:hover": socketConnected ? undefined : { backgroundColor: "rgba(198,40,40,0.18)" },
                                ...(isMobile && socketConnected && {
                                    "& .MuiChip-label": { display: "none" },
                                    "& .MuiChip-icon": { mx: "8px !important" },
                                }),
                            }}
                        />
                    </Tooltip>

                    {
                        canAccessMitraContacts && (
                            <Tooltip title="Daftar Kontak Mitra — mulai chat duluan">
                                <IconButton onClick={() => setContactsDialogOpen(true)} size="small" sx={{ color: "text.secondary" }}>
                                    <PeopleAltIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )
                    }

                    {
                        // DEV-1213/1214 — dashboard performa Corporate, khusus Leader/SPV (dicek juga di backend).
                        Global.isCorporateDashboardUser() && (
                            <Tooltip title="Dashboard Performa Corporate">
                                <IconButton onClick={() => setDashboardDialogOpen(true)} size="small" sx={{ color: "text.secondary" }}>
                                    <BarChartIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )
                    }

                    {/* Nama user & App Profile login selalu terlihat; diklik membuka menu aplikasi
                        (menu menutup sendiri saat item diklik, lihat AgentIdentityMenu). */}
                    <AgentIdentityMenu brands={brands} availability={autoAssign.availability} compact={isMobile}>
                        {
                            // DEV-1238 — atur spesialisasi produk sales, khusus admin & hanya kalau ada brand auto-assign.
                            can(LIVECHAT_PERMISSION.AUTOLEADS_MANAGE) && autoAssignBrandSet.size > 0 && (
                                <MenuItem onClick={() => setLeadSalesDialogOpen(true)}>
                                    <ListItemIcon><ManageAccountsOutlinedIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText primary="Kelola Sales Live Chat" />
                                </MenuItem>
                            )
                        }
                        {
                            // DEV-1234 — khusus root-administrator & it-database.
                            access.canManageAccess && (
                                <MenuItem onClick={() => setAccessLevelDialogOpen(true)}>
                                    <ListItemIcon><AdminPanelSettingsOutlinedIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText primary="Level Akses Live Chat" />
                                </MenuItem>
                            )
                        }
                        {
                            can(LIVECHAT_PERMISSION.BRAND_SETTINGS) && (
                                <MenuItem onClick={() => setSettingsDialogOpen(true)}>
                                    <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText primary="Settings" />
                                </MenuItem>
                            )
                        }
                        {(can(LIVECHAT_PERMISSION.AUTOLEADS_MANAGE) || can(LIVECHAT_PERMISSION.BRAND_SETTINGS) || access.canManageAccess) && <Divider />}
                        <MenuItem onClick={handleLogout}>
                            <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary="Logout" />
                        </MenuItem>
                    </AgentIdentityMenu>
                </Toolbar>
            </AppBar>

            <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
                {(!isMobile || !selectedSession) && (
                    <ChannelRail
                        brands={brands}
                        activeBrand={activeBrand}
                        onSelect={setActiveBrand}
                        unreadByBrand={channelUnread}
                    />
                )}

                {(!isMobile || !selectedSession) && (
                    <ConversationList
                        sessions={sessions}
                        selectedSessionId={selectedSession?._id}
                        onSelect={handleSelectSession}
                        filter={filter}
                        onFilterChange={setFilter}
                        search={search}
                        onSearchChange={setSearch}
                        viewerId={viewerId}
                        filterCounts={isCsTeam ? { closed: awaitingCsCount } : undefined}
                        showAwaitingCs={isCsTeam}
                        headerSlot={showOwnershipTabs && (
                            <OwnershipTabs
                                value={ownership}
                                onChange={setOwnership}
                                isSupervisor={autoAssign.availability.isSupervisor}
                                agents={autoAssign.agents}
                                agentId={agentFilter}
                                onAgentChange={setAgentFilter}
                            />
                        )}
                        renderBadges={(s) => (
                            <AssignmentBadge
                                session={s}
                                viewerId={viewerId}
                                isAutoAssignBrand={autoAssignBrandSet.has(s.brand)}
                                showAwaitingCs={isCsTeam}
                            />
                        )}
                    />
                )}

                {(!isMobile || selectedSession) && (
                    <ChatPanel
                        // key per sesi: draft teks & lampiran yang belum terkirim tidak ikut terbawa ke
                        // percakapan lain saat pindah sesi.
                        key={selectedSession?._id || "empty"}
                        canClaim={canClaimSelected}
                        claiming={claiming}
                        onClaimSession={handleClaimSession}
                        session={panelSession}
                        messages={messages}
                        hasMoreMessages={messagesHasMore}
                        loadingOlderMessages={loadingOlderMessages}
                        onLoadOlderMessages={handleLoadOlderMessages}
                        onSendMessage={handleSendMessage}
                        onSendAttachment={handleSendAttachment}
                        onRetryMessage={handleRetry}
                        onCloseSession={handleCloseSession}
                        onOpenLabelDialog={() => setLabelDialogOpen(true)}
                        canReply={canForSession(selectedSession, LIVECHAT_PERMISSION.SESSION_REPLY)}
                        canLabel={canForSession(selectedSession, LIVECHAT_PERMISSION.SESSION_LABEL)}
                        canClose={canForSession(selectedSession, LIVECHAT_PERMISSION.SESSION_CLOSE)}
                        canTransfer={canTransferSelected}
                        onOpenTransfer={() => setTransferDialogOpen(true)}
                        isMobile={isMobile}
                        onBack={handleCloseChat}
                        onCloseChat={handleCloseChat}
                        showContextPanelToggle={!isMobile}
                        contextPanelOpen={contextPanelOpen}
                        onToggleContextPanel={() => setContextPanelOpen((prev) => !prev)}
                    />
                )}

                {selectedSession && !isMobile && contextPanelOpen && (
                    <CustomerContextPanel
                        session={panelSession}
                        onGenerateLink={handleGenerateLink}
                        canViewOrderInfo={canForSession(selectedSession, LIVECHAT_PERMISSION.SESSION_ORDER_INFO)}
                        canGenerateLink={canForSession(selectedSession, LIVECHAT_PERMISSION.SESSION_GENERATE_LINK)}
                        onClose={() => setContextPanelOpen(false)}
                    />
                )}
            </Box>

            <SetLabelDialog
                open={labelDialogOpen}
                session={selectedSession}
                onClose={() => setLabelDialogOpen(false)}
                onSaved={(updated) => { setSelectedSession(updated); loadInbox(); }}
            />

            {canTransferSelected && (
                <TransferChatDialog
                    open={transferDialogOpen}
                    session={selectedSession}
                    onClose={() => setTransferDialogOpen(false)}
                    onSubmit={handleTransferSession}
                />
            )}

            <GenerateLinkDialog
                open={generatedLink !== null}
                url={generatedLink}
                onClose={() => setGeneratedLink(null)}
            />

            <LiveChatSettingsDialog
                open={settingsDialogOpen}
                onClose={() => setSettingsDialogOpen(false)}
                onChanged={loadBrands}
            />

            <LiveChatDashboardDialog
                open={dashboardDialogOpen}
                onClose={() => setDashboardDialogOpen(false)}
            />

            {can(LIVECHAT_PERMISSION.AUTOLEADS_MANAGE) && (
                <LeadSalesDialog open={leadSalesDialogOpen} onClose={() => setLeadSalesDialogOpen(false)} />
            )}

            {access.canManageAccess && (
                <AccessLevelDialog open={accessLevelDialogOpen} onClose={() => setAccessLevelDialogOpen(false)} />
            )}

            <MitraContactsDialog
                open={contactsDialogOpen}
                onClose={() => setContactsDialogOpen(false)}
                onStarted={handleMitraSessionStarted}
            />

            <Snackbar
                open={!!(notice || autoAssign.error)}
                autoHideDuration={4000}
                onClose={() => { setNotice(null); autoAssign.clearError(); }}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={autoAssign.error ? "error" : notice?.severity || "info"}
                    onClose={() => { setNotice(null); autoAssign.clearError(); }}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {autoAssign.error || notice?.text}
                </Alert>
            </Snackbar>
        </Box>
    );
}
